import * as React from 'react';
import { styled } from '@linaria/react';

import { clamp, roundTo } from '@/utils';
import useGrabSfx from '@/hooks/use-grab-sfx';
import useTickSfx from '@/hooks/use-tick-sfx';
import useBoundingBox from '@/hooks/use-bounding-box';

import Demo from '@/components/Demo';
import SliderControl from '@/components/Controls/SliderControl';
import ToggleButtonGroupControl from '@/components/Controls/ToggleButtonGroupControl';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import SvgWithGrid from '@/components/SvgWithGrid';

const INITIAL_X = 2;
const INITIAL_Y = 4;
const VIEWBOX_SIZE = 16;

function RelativeCommandsDemo() {
  const [coordStyle, setCoordStyle] = React.useState<
    'absolute' | 'relative'
  >('absolute');

  const [isDragging, setIsDragging] = React.useState(false);
  const [xAdjust, setXAdjust] = React.useState(INITIAL_X);
  const [yAdjust, setYAdjust] = React.useState(INITIAL_Y);

  const xAdjustRef = React.useRef(INITIAL_X);
  xAdjustRef.current = xAdjust;
  const yAdjustRef = React.useRef(INITIAL_Y);
  yAdjustRef.current = yAdjust;

  const svgRef = React.useRef<SVGSVGElement>(null);
  const svgBox = useBoundingBox(svgRef);
  const multiplier = svgBox?.width ? svgBox.width / VIEWBOX_SIZE : 1;

  const [playStartGrab, playReleaseGrab] = useGrabSfx();
  const playTick = useTickSfx();

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!svgBox) {
        return;
      }

      let x = (event.clientX - svgBox.left) / multiplier;
      let y = (event.clientY - svgBox.top) / multiplier;

      x = Math.round(clamp(x, 0, VIEWBOX_SIZE));
      y = Math.round(clamp(y, 0, VIEWBOX_SIZE));

      const isChangingPosition =
        x !== xAdjustRef.current || y !== yAdjustRef.current;

      if (!isChangingPosition) {
        return;
      }

      playTick();

      setXAdjust(x);
      setYAdjust(y);
    };

    const handlePointerUp = () => {
      document.body.style.cursor = 'default';
      playReleaseGrab();
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, svgBox, multiplier, playReleaseGrab, playTick]);

  const handleStartDragging = () => {
    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
    playStartGrab();
  };

  const handleKeyPress = (key: string) => {
    let nudgeX = 0;
    let nudgeY = 0;

    if (key === 'ArrowLeft') {
      nudgeX = -1;
    } else if (key === 'ArrowRight') {
      nudgeX = 1;
    } else if (key === 'ArrowUp') {
      nudgeY = -1;
    } else if (key === 'ArrowDown') {
      nudgeY = 1;
    }

    setXAdjust(clamp(xAdjustRef.current + nudgeX, 0, VIEWBOX_SIZE));
    setYAdjust(clamp(yAdjustRef.current + nudgeY, 0, VIEWBOX_SIZE));
  };

  const handleReset = () => {
    setXAdjust(INITIAL_X);
    setYAdjust(INITIAL_Y);
  };

  return (
    <Demo
      outputWrapper={null}
      initialValues={{}}
      controlPosition="bottom"
      renderAltControls={() => (
        <ToggleButtonGroupControl
          inline
          id="coordStyle"
          label="Command Type"
          value={coordStyle}
          setValue={(value) =>
            setCoordStyle(value as 'absolute' | 'relative')
          }
          options={[
            { label: 'Absolute', value: 'absolute' },
            { label: 'Relative', value: 'relative' },
          ]}
        />
      )}
      CustomGridComponent={ControlsGrid}
      controls={[
        <SliderControl
          key="xAdjust"
          id="xAdjust"
          label="Starting X position"
          min={0}
          max={VIEWBOX_SIZE}
          step={0.1}
          value={xAdjust}
          setValue={setXAdjust}
          handleReset={() => setXAdjust(4)}
        />,
        <SliderControl
          key="yAdjust"
          id="yAdjust"
          label="Starting Y position"
          min={0}
          max={VIEWBOX_SIZE}
          step={0.1}
          value={yAdjust}
          setValue={setYAdjust}
          handleReset={() => setYAdjust(INITIAL_Y)}
        />,
      ]}
    >
      {({}) => {
        const path = calculatePath(xAdjust, yAdjust, coordStyle);
        const snippet = `\
<svg viewBox="0 0 16 16">
  <path
    d="${path}"
  />
</svg>
        `;

        return (
          <Wrapper>
            <Col>
              <SvgWrapper>
                <Svg
                  ref={svgRef}
                  gridGap={1}
                  lineWidth={0.1}
                  viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
                >
                  <Path d={path} />
                </Svg>
                <Handle
                  onPointerDown={() => {
                    handleStartDragging();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Delete') {
                      handleReset();
                    } else if (event.key.includes('Arrow')) {
                      event.preventDefault();
                      handleKeyPress(event.key);
                    }
                  }}
                  style={{
                    top: yAdjust * multiplier,
                    left: xAdjust * multiplier,
                  }}
                >
                  <PointLabel draggable={false}>
                    ({xAdjust}, {yAdjust})
                  </PointLabel>
                </Handle>
              </SvgWrapper>
            </Col>
            <Col>
              <CodeWrapper>
                <ClientOnlySnippet
                  omitCopyButton
                  lockedToColorMode="dark"
                  lang="html"
                  code={snippet}
                />
              </CodeWrapper>
            </Col>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

function calculatePath(
  xAdjust: number,
  yAdjust: number,
  coordStyle: 'absolute' | 'relative'
) {
  const firstX = roundTo(xAdjust, 1);
  const firstY = roundTo(yAdjust, 1);

  if (coordStyle === 'absolute') {
    return `
      M ${firstX},${firstY}
      L 6,12
      L 8,8
      L 10,12
      L 12,4
    `;
  } else {
    return `
      M ${firstX},${firstY}
      l 2,8
      l 2,-4
      l 2,4
      l 2,-8
    `;
  }
}

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;

  @media (max-width: 28rem) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  gap: 16px;
  padding-block: 16px;

  @media (max-width: 47.8125rem) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;

const Col = styled.div`
  flex: 1;

  @media (max-width: 47.8125rem) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const SvgWrapper = styled.div`
  position: relative;
`;

const Svg = styled(SvgWithGrid)`
  display: block;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1;
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
`;

const Path = styled.path`
  fill: none;
  stroke: var(--wham-primary-500);
  stroke-width: 1;
`;

const CodeWrapper = styled.div`
  padding: 0;
  width: 100%;
  height: 100%;
  outline: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const Handle = styled.button`
  position: absolute;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-text);
  transform: translate(-50%, -50%);
  border: 4px solid var(--color-background);
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const PointLabel = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  white-space: nowrap;
  transform: translate(-50%, -100%);
  background: var(--color-background);
  user-select: none;
  font-size: 0.75rem;
  font-family: var(--font-family-mono);
  font-weight: 500;
  color: white;
  padding: 2px 4px;
  border-radius: 4px;
  pointer-events: none;
  opacity: 0;
  transition:
    opacity 500ms,
    color 200ms;

  ${Handle}:active &,
  ${Handle}:focus-visible & {
    color: var(--wham-primary-500);
    transition:
      opacity 100ms,
      color 100ms;
    opacity: 1;
  }
`;

export default RelativeCommandsDemo;
